import { expectDomTypeError } from '@milkdown/exception'
import { headingSchema } from '@milkdown/kit/preset/commonmark'

import { withMeta } from '../__internal__'
import { buildPandocAttrString } from '../plugin/remark-heading-attrs-stringify-plugin'
import type { HProperties } from '../plugin/remark-heading-attrs-stringify-plugin'

/// This schema extends the heading schema from commonmark to add Pandoc-style attributes.
/// Pandoc syntax: # Heading {#id .class key=value}
export const extendHeadingSchemaForAttrs = headingSchema.extendSchema(
  (prev) => {
    return (ctx) => {
      const baseSchema = prev(ctx)
      return {
        ...baseSchema,
        attrs: {
          ...baseSchema.attrs,
          class: {
            default: null,
          },
          // Additional attributes stored as JSON
          dataAttrs: {
            default: null,
          },
          // Original attribute order for round-trip fidelity (JSON array of token types)
          // e.g. ["key:epub:type", "class:titlepage", "class:page-end"]
          attrOrder: {
            default: null,
          },
        },
        parseDOM: [
          {
            tag: 'h1',
            getAttrs: (dom) => getAttrsFromDom(dom, 1),
          },
          {
            tag: 'h2',
            getAttrs: (dom) => getAttrsFromDom(dom, 2),
          },
          {
            tag: 'h3',
            getAttrs: (dom) => getAttrsFromDom(dom, 3),
          },
          {
            tag: 'h4',
            getAttrs: (dom) => getAttrsFromDom(dom, 4),
          },
          {
            tag: 'h5',
            getAttrs: (dom) => getAttrsFromDom(dom, 5),
          },
          {
            tag: 'h6',
            getAttrs: (dom) => getAttrsFromDom(dom, 6),
          },
        ],
        toDOM: (node) => {
          const attrs: Record<string, string> = {}
          if (node.attrs.id) attrs.id = node.attrs.id
          if (node.attrs.class) attrs.class = node.attrs.class

          // Add data attributes
          if (node.attrs.dataAttrs) {
            try {
              const dataAttrs = JSON.parse(node.attrs.dataAttrs) as Record<
                string,
                string
              >
              for (const [key, value] of Object.entries(dataAttrs)) {
                // Keep epub:type and similar namespaced attributes as-is
                // Store others as data- attributes
                if (key.includes(':')) {
                  attrs[`data-${key.replace(':', '-')}`] = value
                } else {
                  attrs[`data-${key}`] = value
                }
              }
            } catch {
              // Ignore invalid JSON
            }
          }

          return [`h${node.attrs.level}`, attrs, 0]
        },
        parseMarkdown: {
          match: ({ type }) => type === 'heading',
          runner: (state, node, type) => {
            try {
              const depth = node.depth as number
              const data = node.data as
                | { hProperties?: Record<string, unknown> }
                | undefined
              const hProperties = data?.hProperties

              let id: string | null = null
              let className: string | null = null
              let dataAttrs: string | null = null
              let attrOrder: string | null = null

              if (hProperties) {
                if (typeof hProperties.id === 'string') {
                  id = hProperties.id
                }
                if (typeof hProperties.className === 'string') {
                  className = hProperties.className
                } else if (Array.isArray(hProperties.className)) {
                  className = hProperties.className.join(' ')
                }

                // Extract other attributes (like epub:type)
                const otherAttrs: Record<string, string> = {}
                for (const [key, value] of Object.entries(hProperties)) {
                  if (key !== 'id' && key !== 'className' && key !== '_attrOrder' && typeof value === 'string') {
                    otherAttrs[key] = value
                  }
                }
                if (Object.keys(otherAttrs).length > 0) {
                  dataAttrs = JSON.stringify(otherAttrs)
                }

                // Preserve original attribute order if available
                if (Array.isArray(hProperties._attrOrder)) {
                  attrOrder = JSON.stringify(hProperties._attrOrder)
                }
              }

              state.openNode(type, { level: depth, id, class: className, dataAttrs, attrOrder })
              state.next(node.children)
              state.closeNode()
            } catch (error) {
              console.error('❌ Error parsing heading:', error)
              // Fallback: create heading without attributes
              const depth = node.depth as number
              state.openNode(type, { level: depth, id: null, class: null, dataAttrs: null })
              state.next(node.children)
              state.closeNode()
            }
          },
        },
        toMarkdown: {
          match: (node) => node.type.name === 'heading',
          runner: (state, node) => {
            // Build hProperties from ProseMirror attrs
            const hProperties: HProperties = {}
            if (node.attrs.id) hProperties.id = node.attrs.id
            if (node.attrs.class) hProperties.className = node.attrs.class

            // Add data attributes (epub:type, etc.)
            if (node.attrs.dataAttrs) {
              try {
                const dataAttrs = JSON.parse(node.attrs.dataAttrs) as Record<
                  string,
                  string
                >
                Object.assign(hProperties, dataAttrs)
              } catch {
                // Ignore invalid JSON
              }
            }

            // Restore original attribute order if available
            if (node.attrs.attrOrder) {
              try {
                hProperties._attrOrder = JSON.parse(node.attrs.attrOrder)
              } catch {
                // Ignore invalid JSON
              }
            }

            // Build the Pandoc attribute string (e.g. "{epub:type=bibliography .bibliography}")
            const attrString = buildPandocAttrString(hProperties)

            state.openNode('heading', undefined, {
              depth: node.attrs.level,
            })

            // Serialize heading text content
            state.next(node.content)

            // Append attribute string directly to the MDAST text.
            // This is done here (during serialization) rather than in a remark
            // transformer, because remark.stringify() only runs the compiler,
            // NOT transformers. A transformer would run during parsing instead.
            if (attrString) {
              state.addNode('text', undefined, ` ${attrString}`)
            }

            state.closeNode()
          },
        },
      }
    }
  }
)

function getAttrsFromDom(dom: Node | string, level: number) {
  if (!(dom instanceof HTMLElement)) throw expectDomTypeError(dom)

  const dataAttrs: Record<string, string> = {}
  for (const attr of dom.attributes) {
    if (attr.name.startsWith('data-')) {
      let key = attr.name.slice(5)
      // Convert data-epub-type back to epub:type
      if (key.startsWith('epub-')) {
        key = 'epub:' + key.slice(5)
      }
      dataAttrs[key] = attr.value
    }
  }

  return {
    level,
    id: dom.id || null,
    class: dom.className || null,
    dataAttrs: Object.keys(dataAttrs).length > 0 ? JSON.stringify(dataAttrs) : null,
  }
}

withMeta(extendHeadingSchemaForAttrs.node, {
  displayName: 'NodeSchema<headingWithAttrs>',
  group: 'Heading',
})

withMeta(extendHeadingSchemaForAttrs.ctx, {
  displayName: 'NodeSchemaCtx<headingWithAttrs>',
  group: 'Heading',
})
